import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/navigation/Header';
import VehicleTypeSelector from './components/VehicleTypeSelector';
import RouteInputForm from './components/RouteInputForm';
import CalculationResults from './components/CalculationResults';
import AdvancedOptions from './components/AdvancedOptions';
import Icon from '../../components/AppIcon';
import { LoadingSpinner, SkeletonBlock } from '../../components/SkeletonLoader';

const TollCalculator = () => {
  const [selectedVehicleType, setSelectedVehicleType] = useState('ev');
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculationResults, setCalculationResults] = useState(null);
  const [saveNotification, setSaveNotification] = useState(null);
  const [advancedOptions, setAdvancedOptions] = useState({
    timeOfDay: 'day',
    showAlternativeRoutes: false,
    avoidTolls: false
  });

  const calculateTollCosts = (routeData) => {
    setIsCalculating(true);

    setTimeout(() => {
      const mockTollPoints = [
        {
          name: 'Festningstunnelen',
          location: 'Oslo sentrum',
          cost: selectedVehicleType === 'ev' ? 14.5 : selectedVehicleType === 'hybrid' ? 21.75 : 29,
          originalCost: 29
        },
        {
          name: 'Smestad',
          location: 'Oslo vest',
          cost: selectedVehicleType === 'ev' ? 11 : selectedVehicleType === 'hybrid' ? 16.5 : 22,
          originalCost: 22
        },
        {
          name: 'Lysaker',
          location: 'Bærum',
          cost: selectedVehicleType === 'ev' ? 12.5 : selectedVehicleType === 'hybrid' ? 18.75 : 25,
          originalCost: 25
        },
        {
          name: 'Asker',
          location: 'Asker sentrum',
          cost: selectedVehicleType === 'ev' ? 9.5 : selectedVehicleType === 'hybrid' ? 14.25 : 19,
          originalCost: 19
        },
        {
          name: 'Drammen',
          location: 'Drammen øst',
          cost: selectedVehicleType === 'ev' ? 13 : selectedVehicleType === 'hybrid' ? 19.5 : 26,
          originalCost: 26
        }
      ];

      const originalCost = mockTollPoints?.reduce((sum, point) => sum + point?.originalCost, 0);
      const totalCost = mockTollPoints?.reduce((sum, point) => sum + point?.cost, 0);
      const discount = originalCost - totalCost;
      const discountPercentage = selectedVehicleType === 'ev' ? 50 : selectedVehicleType === 'hybrid' ? 25 : 0;

      const results = {
        departure: routeData?.departure,
        destination: routeData?.destination,
        vehicleType: selectedVehicleType,
        distance: 65,
        duration: '1t 15min',
        tollPoints: mockTollPoints,
        originalCost,
        totalCost,
        discount,
        discountPercentage
      };

      setCalculationResults(results);
      setIsCalculating(false);
    }, 1500);
  };

  const saveTripToHistory = () => {
    if (!calculationResults) return;

    try {
      const existingRaw = localStorage.getItem('tollCalculatorHistory');
      let existing = [];
      if (existingRaw) {
        const parsed = JSON.parse(existingRaw);
        if (Array.isArray(parsed)) existing = parsed;
      }

      const newTrip = {
        id: `trip-${Date.now()}`,
        date: new Date()?.toISOString(),
        departure: calculationResults?.departure,
        destination: calculationResults?.destination,
        vehicleType: calculationResults?.vehicleType,
        distance: calculationResults?.distance,
        duration: calculationResults?.duration,
        originalCost: calculationResults?.originalCost,
        totalCost: calculationResults?.totalCost,
        discount: calculationResults?.discount,
        discountPercentage: calculationResults?.discountPercentage,
        tollPoints: calculationResults?.tollPoints,
      };

      const updated = [newTrip, ...existing];
      localStorage.setItem('tollCalculatorHistory', JSON.stringify(updated));

      setSaveNotification({ type: 'success', message: 'Tur lagret i reisehistorikk!' });
      setTimeout(() => setSaveNotification(null), 3500);
    } catch (_) {
      setSaveNotification({ type: 'error', message: 'Kunne ikke lagre turen. Prøv igjen.' });
      setTimeout(() => setSaveNotification(null), 3500);
    }
  };

  const handleNewCalculation = () => {
    setCalculationResults(null);
  };

  return (
    <>
      <Helmet>
        <title>Bomkalkulator - Veiassistent</title>
        <meta name="description" content="Beregn bomkostnader for din reise med rabatter for elektriske og hybride kjøretøy. Planlegg ruten din og optimaliser kostnader." />
      </Helmet>
      <Header />
      {/* Save notification toast */}
      {saveNotification && (
        <div
          className="fixed top-24 right-4 z-50 flex items-center gap-3 px-5 py-4 shadow-lg"
          style={{
            background: saveNotification?.type === 'success' ? 'var(--color-success, #10B981)' : '#EF4444',
            borderRadius: 'var(--radius-sm)',
            color: '#fff',
            minWidth: '280px',
            animation: 'slideInRight 0.3s ease-out',
          }}
        >
          <Icon
            name={saveNotification?.type === 'success' ? 'CheckCircle' : 'AlertCircle'}
            size={20}
            color="#fff"
          />
          <span className="text-sm font-medium">{saveNotification?.message}</span>
        </div>
      )}
      <main className="min-h-screen bg-background pt-[84px] pb-8 md:pb-12 lg:pb-16">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8">
          <div className="mb-6 md:mb-8 lg:mb-12">
            <div className="flex items-center gap-3 mb-3 md:mb-4">
              <div
                className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center"
                style={{ background: 'rgba(0, 79, 152, 0.1)', borderRadius: 'var(--radius-sm)' }}
              >
                <Icon name="Calculator" size={window.innerWidth >= 768 ? 24 : 20} color="var(--color-primary)" />
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground font-heading">
                Bomkalkulator
              </h1>
            </div>
            <p className="text-sm md:text-base lg:text-lg text-muted-foreground font-body max-w-3xl">
              Beregn nøyaktige bomkostnader for din reise med automatiske rabatter for elektriske og hybride kjøretøy. Planlegg ruten din og optimaliser kostnader.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
            <div className="lg:col-span-5 space-y-6 md:space-y-8">
              <div className="bg-card border-2 border-border p-4 md:p-6 lg:p-8" style={{ borderRadius: 'var(--radius-md)' }}>
                <VehicleTypeSelector
                  selectedType={selectedVehicleType}
                  onTypeChange={setSelectedVehicleType}
                />
              </div>

              <div className="bg-card border-2 border-border p-4 md:p-6 lg:p-8" style={{ borderRadius: 'var(--radius-md)' }}>
                <h3 className="text-base md:text-lg font-semibold mb-4 md:mb-6 text-foreground font-heading">
                  Ruteinformasjon
                </h3>
                <RouteInputForm
                  onCalculate={calculateTollCosts}
                  isCalculating={isCalculating}
                />
              </div>

              <AdvancedOptions
                options={advancedOptions}
                onOptionsChange={setAdvancedOptions}
              />

              <div className="bg-card border-2 border-border p-4 md:p-6" style={{ borderRadius: 'var(--radius-sm)' }}>
                <div className="flex items-start gap-3">
                  <Icon name="Lightbulb" size={20} color="var(--color-accent)" className="flex-shrink-0 mt-1" />
                  <div>
                    <div className="text-sm md:text-base font-medium text-foreground mb-2">
                      Tips for å spare penger
                    </div>
                    <ul className="text-xs md:text-sm text-muted-foreground font-caption space-y-1">
                      <li>• Elektriske kjøretøy får 50% rabatt på alle bomstasjoner</li>
                      <li>• Hybride kjøretøy får 25% rabatt på de fleste bomstasjoner</li>
                      <li>• Reis utenfor rushtid for lavere priser</li>
                      <li>• Vurder alternative ruter med færre bomstasjoner</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              {calculationResults ? (
                <div className="bg-card border-2 border-border p-4 md:p-6 lg:p-8" style={{ borderRadius: 'var(--radius-md)' }}>
                  <CalculationResults
                    results={calculationResults}
                    onSaveRoute={saveTripToHistory}
                    onNewCalculation={handleNewCalculation}
                  />
                </div>
              ) : isCalculating ? (
                /* Calculating skeleton */
                (<div className="bg-card border-2 border-border p-4 md:p-6 lg:p-8" style={{ borderRadius: 'var(--radius-md)', minHeight: '400px' }}>
                  {/* Header row */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
                    <SkeletonBlock width="180px" height="22px" />
                    <SkeletonBlock width="100px" height="32px" style={{ borderRadius: 'var(--radius-sm)' }} />
                  </div>
                  {/* Total cost block */}
                  <div
                    style={{
                      background: 'var(--color-muted)',
                      border: '2px solid var(--color-border)',
                      borderRadius: 'var(--radius-md)',
                      padding: '24px',
                      marginBottom: '0',
                    }}
                  >
                    {/* Cost summary */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: '20px', borderBottom: '2px solid var(--color-border)', marginBottom: '20px' }}>
                      <div>
                        <SkeletonBlock width="110px" height="13px" style={{ marginBottom: '10px' }} />
                        <SkeletonBlock width="160px" height="40px" />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
                        <SkeletonBlock width="80px" height="18px" />
                        <SkeletonBlock width="80px" height="18px" />
                      </div>
                    </div>
                    {/* Spinner + label */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '16px 0 8px', gap: '12px' }}>
                      <div
                        style={{
                          width: '52px',
                          height: '52px',
                          borderRadius: '50%',
                          background: 'rgba(255,87,34,0.08)',
                          border: '2px solid rgba(255,87,34,0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <LoadingSpinner size={30} color="#FF5722" />
                      </div>
                      <span
                        style={{
                          fontFamily: 'var(--font-heading)',
                          fontSize: '12px',
                          fontWeight: 700,
                          letterSpacing: '0.1em',
                          textTransform: 'uppercase',
                          color: 'var(--color-muted-foreground)',
                        }}
                      >
                        Beregner bomkostnader...
                      </span>
                    </div>
                    {/* Toll point skeletons */}
                    <div style={{ marginTop: '20px' }}>
                      <SkeletonBlock width="160px" height="16px" style={{ marginBottom: '12px' }} />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {[0, 1, 2, 3, 4]?.map(i => (
                          <div
                            key={i}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '12px 16px',
                              background: 'var(--color-card)',
                              border: '2px solid var(--color-border)',
                              borderRadius: 'var(--radius-sm)',
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <SkeletonBlock width="32px" height="32px" style={{ borderRadius: 'var(--radius-sm)', flexShrink: 0 }} />
                              <div>
                                <SkeletonBlock width={`${90 + i * 15}px`} height="14px" style={{ marginBottom: '5px' }} />
                                <SkeletonBlock width={`${60 + i * 10}px`} height="12px" />
                              </div>
                            </div>
                            <SkeletonBlock width="60px" height="16px" />
                          </div>
                        ))}
                      </div>
                    </div>
                    {/* Summary row */}
                    <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '2px solid var(--color-border)', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <SkeletonBlock width="100px" height="14px" />
                        <SkeletonBlock width="70px" height="14px" />
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '8px', borderTop: '2px solid var(--color-border)' }}>
                        <SkeletonBlock width="120px" height="18px" />
                        <SkeletonBlock width="80px" height="18px" />
                      </div>
                    </div>
                  </div>
                  {/* Action buttons skeleton */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '16px' }}>
                    <SkeletonBlock width="100%" height="44px" style={{ borderRadius: 'var(--radius-sm)' }} />
                    <SkeletonBlock width="100%" height="44px" style={{ borderRadius: 'var(--radius-sm)' }} />
                  </div>
                </div>)
              ) : (
                <div className="bg-card border-2 border-border p-8 md:p-12 lg:p-16 flex flex-col items-center justify-center text-center min-h-[400px] md:min-h-[500px] lg:min-h-[600px]" style={{ borderRadius: 'var(--radius-md)' }}>
                  <div
                    className="w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 flex items-center justify-center mb-4 md:mb-6"
                    style={{ background: 'rgba(0, 79, 152, 0.1)', borderRadius: 'var(--radius-md)' }}
                  >
                    <Icon name="MapPin" size={window.innerWidth >= 1024 ? 48 : window.innerWidth >= 768 ? 40 : 32} color="var(--color-primary)" />
                  </div>
                  <h3 className="text-lg md:text-xl lg:text-2xl font-semibold text-foreground mb-2 md:mb-3 font-heading">
                    Klar til å beregne
                  </h3>
                  <p className="text-sm md:text-base text-muted-foreground max-w-md font-body">
                    Velg kjøretøytype og fyll inn ruteinformasjon for å se detaljerte bomkostnader med automatiske rabatter.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default TollCalculator;